import { CurrencyCode } from 'further-types/lp/vehicle';
import { MagnitudeShorthand } from 'further-types/utils';
import { zeroDecimalCurrencies } from '../labels';

interface NumberToCurrencyStringOptions {
  hidePennies?: boolean;
  hidePenniesIfInteger?: boolean;
  currency?: string;
  unlimitedDp?: boolean;
  fallback?: string;
  minZero?: boolean;
  negativeInBrackets?: boolean;
  showFallbackForZero?: boolean;
}
export const numberToCurrencyDefaultOptions: NumberToCurrencyStringOptions = {
  hidePennies: false,
  hidePenniesIfInteger: false,
  currency: 'GBP',
  unlimitedDp: false,
  showFallbackForZero: false,
  fallback: '',
  minZero: false,
  negativeInBrackets: false,
};

export const numberToCurrencyString = (
  input?: number | null,
  options: NumberToCurrencyStringOptions = numberToCurrencyDefaultOptions,
): string => {
  const isZeroDecimalCurrency =
    zeroDecimalCurrencies.findIndex(
      (zeroDecimalCurrency) => zeroDecimalCurrency === options.currency,
    ) !== -1;
  const {
    hidePennies = isZeroDecimalCurrency ||
      numberToCurrencyDefaultOptions.hidePennies,
    hidePenniesIfInteger = numberToCurrencyDefaultOptions.hidePenniesIfInteger,
    currency = numberToCurrencyDefaultOptions.currency,
    unlimitedDp = numberToCurrencyDefaultOptions.unlimitedDp,
    fallback = '',
    minZero = numberToCurrencyDefaultOptions.minZero,
    negativeInBrackets = numberToCurrencyDefaultOptions.negativeInBrackets,
  } = options;

  const showFallback =
    input === null ||
    input === undefined ||
    (options.showFallbackForZero && input === 0);

  if (showFallback) return fallback;

  let inputNumber = typeof input === 'number' ? input : Number(input);

  if (minZero && inputNumber < 0) {
    inputNumber = 0;
  }

  const isInteger = Number.isInteger(inputNumber);

  const fractionDigitsConfig =
    (isInteger && !!hidePenniesIfInteger) || !!hidePennies
      ? {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }
      : {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        };

  let output: string;
  if (unlimitedDp) {
    output = inputNumber.toLocaleString(undefined, {
      maximumFractionDigits: 20, // just set a really high number for maximumFractionDigits
      style: 'currency',
      currency,
    });
  } else {
    output = inputNumber.toLocaleString(undefined, {
      ...fractionDigitsConfig,
      style: 'currency',
      currency,
    });
  }

  if (output.startsWith('US$')) {
    return output.replace(/^US\$/, '$');
  }

  if (negativeInBrackets && inputNumber < 0) {
    return `(${output.replace('-', '')})`;
  }

  return output;
};

type NumberToShorthandCurrencyStringOptions = {
  currency?: CurrencyCode;
  negativeInBrackets?: boolean;
};

const numberToShorthandCurrencyStringDefaultOptions: NumberToShorthandCurrencyStringOptions =
  {
    currency: CurrencyCode.GBP,
    negativeInBrackets: false,
  };

export const numberToShorthandCurrencyString = (
  input: number,
  options: NumberToShorthandCurrencyStringOptions = numberToShorthandCurrencyStringDefaultOptions,
) => {
  const suffixes = [
    '',
    MagnitudeShorthand.Thousand,
    MagnitudeShorthand.Million,
    MagnitudeShorthand.Billion,
    MagnitudeShorthand.Trillion,
  ];
  const {
    currency = numberToShorthandCurrencyStringDefaultOptions.currency,
    negativeInBrackets = numberToShorthandCurrencyStringDefaultOptions.negativeInBrackets,
  } = options;

  if (Math.abs(input) < 1000) {
    return numberToCurrencyString(input, {
      hidePennies: true,
      currency,
      negativeInBrackets,
    });
  }

  const suffixIndex = Math.floor(Math.log10(Math.abs(input)) / 3);
  const clampedSuffixIndex = Math.min(suffixIndex, suffixes.length - 1);
  const scaledValue = input / Math.pow(10, clampedSuffixIndex * 3);

  const formattedValue = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(scaledValue);
  const suffix = suffixes[clampedSuffixIndex];
  const formattedValueWithSuffix = formattedValue + suffix;

  return negativeInBrackets && input < 0
    ? `(${formattedValueWithSuffix.replace('-', '')})`
    : formattedValueWithSuffix;
};
